/**
 * TOSS Web Application - Client portal navigation configuration
 *
 * The customer portal's menu — six items at release Р2, «Шаблоны» from release Р5
 * (r5-application-templates.md §5), and «Контейнеры» / «ПТО» from release Р4
 * (r4-fleet-is-scoped-to-our-own-service-history.md §6) — nine items in total. ONE array,
 * three renderers —
 * `ClientSidebar` (desktop / tablet), `ClientBottomNav` (phone) and `ClientHeader` (page
 * title) — so the three can never drift apart on what "the menu" is.
 *
 * A `.ts` file, not `.tsx`, on purpose: `react-refresh/only-export-components` fires on
 * any module that mixes component and non-component exports, and two of the six warnings
 * in the lint baseline come from exactly that in `Sidebar.tsx`. Config stays out of
 * component files.
 *
 * **Every item deliberately leaves `permission`, `anyOf` and `allowedRoles` unset.**
 * `isNavItemVisible()` returns `true` only for an item that declares none of them, and
 * for a client session `permissionStore.fetchMyKeys()` resolves to an EMPTY Set (not
 * `null`): the null-guard passes, and any keyed item would then be filtered out and the
 * customer would face an empty menu. The portal gates on role at the route level
 * (`ClientRoute`, decision Р31) — never on keys, which this audience never receives.
 *
 * ---------------------------------------------------------------------------
 * TWO FLAGS Р2 ADDED, AND THE BUGS THEY EXIST TO PREVENT
 * ---------------------------------------------------------------------------
 *
 * **`exact`** — «Главная» lives at `/client`, which is a PREFIX of every other portal
 * path. {@link isClientNavItemActive} was `pathname === path || pathname.startsWith(path
 * + '/')`, so without this flag «Главная» would match `/client/applications`,
 * `/client/documents`, `/client/profile` … — every screen. Two visible consequences, the
 * second worse than the first: the sidebar and the bottom bar would highlight two items
 * at once, and `ClientHeader` takes its title from `clientNavigationItems.find(...)`,
 * i.e. the FIRST match in array order, which would title every screen «Главная».
 *
 * **`showInBottomNav`** — nine items after Р4, and the phone bar still holds only five.
 * Bottom-nav items are `flex-1`, so a tenth would not break the layout in any way anyone
 * would notice: it would silently squeeze five labels into truncated stubs. «Уведомления»
 * is the one that dropped out first, and it is not lost — `ClientHeader` renders
 * `ClientNotificationsBell` on the phone too (before Р2 the bell was behind
 * `!showMobileNav`, precisely because the bar carried the same unread count).
 *
 * Р5's «Шаблоны» was the first item to need this flag beyond «Уведомления», for the same
 * reason (the bar's only spare visual room was already spent). Р4's «Контейнеры» and «ПТО»
 * carry it too.
 *
 * ---------------------------------------------------------------------------
 * THE FIFTH SLOT IS A SCREEN AGAIN (mobile rework, 2026-08-21)
 * ---------------------------------------------------------------------------
 *
 * Release Р4 spent the fifth slot on «Ещё» — a synthetic trigger opening a sheet that listed
 * the five items sitting at `showInBottomNav: false`, «Профиль» among them. A sheet is not a
 * place: it has no address, nothing highlights while it is open, the device's back control
 * does not close it, and returning to what it hid means finding the trigger again.
 *
 * The fifth slot is now «Профиль» itself, and the four items it hid live ON that screen —
 * {@link clientProfileHubItems} is the list `ClientProfilePage` renders as its «Кабинет»
 * menu. Derived here rather than hand-written there for the same reason
 * {@link clientBottomNavItems} is: a second copy is how the hub and the sidebar end up
 * disagreeing about a label, an icon or a path.
 *
 * The bar is «Главная · Заявки · Документы · Сообщения · Профиль» — the exact five it
 * carried after Р2, before «Ещё» displaced «Профиль».
 */

import {
  Bell,
  Building2,
  Container,
  Copy,
  FileText,
  FolderOpen,
  Home,
  Library,
  MapPin,
  MessagesSquare,
  ShieldCheck,
  User,
  Users,
} from 'lucide-react';
import { ROUTES } from '@/constants';
import { CPK, type ClientPermissionKey } from '@/constants/clientPermissions';
import type { NavItem } from '../sidebar-nav/navigationConfig';

// ============================================================================
// Types
// ============================================================================

/**
 * A portal menu item: the shared {@link NavItem} plus the two portal-only flags.
 *
 * Both are optional and both default to the pre-Р2 behaviour (`exact: false` — prefix
 * match; `showInBottomNav: true` — present on the phone), so an item that declares
 * neither behaves exactly as the four Р1 items did.
 */
export interface ClientNavItem extends NavItem {
  /**
   * Match the path EXACTLY instead of as a prefix. Set on «Главная» (`/client`), whose
   * path is a prefix of every other portal route — see the module comment.
   */
  exact?: boolean;
  /**
   * Whether the phone's bottom bar carries this item. `false` keeps it in the sidebar
   * only. Defaults to `true`.
   */
  showInBottomNav?: boolean;
  /**
   * Which block of the computer's sidebar the item sits in. Defaults to `work`.
   *
   * Nine equal rows read as a list of everything the software can do; the same nine in three
   * named blocks read as "what I do", "what I look up", "me". The split is the one the phone
   * already makes — the bar's five are the daily work, the profile hub holds the rest — so this
   * is that decision written down, not a second navigation.
   */
  group?: ClientNavGroupId;
  /**
   * A CLIENT permission key (`CPK.*`) the caller must hold for this item to be drawn.
   *
   * **A new field rather than the existing `permission` / `anyOf` / `allowedRoles`,** and that is
   * load-bearing. `isNavItemVisible()` filters on those three against `permissionStore`, which
   * resolves to an EMPTY set for a client session by design (decision Р31) — so an item using them
   * would vanish from the customer's menu entirely. `clientNavigationConfig.test.ts` asserts that
   * no portal item names one of the three, and that assertion must keep passing untouched.
   *
   * Evaluated by {@link isClientNavItemVisible} with the caller's own capabilities, which the
   * sidebar and the profile hub read from `useClientCapabilities`.
   */
  requiresClientPermission?: ClientPermissionKey;
}

/** The blocks of the sidebar, in display order. */
export type ClientNavGroupId = 'work' | 'reference' | 'company' | 'personal';

// ============================================================================
// Constants
// ============================================================================

/**
 * How many items the phone bar can hold before labels start truncating.
 *
 * Not a hard limit anything enforces at runtime — `ClientBottomNav` maps whatever
 * {@link clientBottomNavItems} contains — but the number the split was made against.
 * Five 72px slots is what a 360px screen fits with a readable 12px label.
 */
export const CLIENT_BOTTOM_NAV_MAX_ITEMS = 5;

// ============================================================================
// Menu
// ============================================================================

/**
 * Client portal menu, in display order. The sidebar renders every item; the phone's
 * bottom bar renders {@link clientBottomNavItems} (this list minus «Шаблоны»,
 * «Контейнеры», «ПТО» and «Уведомления» — all four `showInBottomNav: false`), and those
 * four are exactly {@link clientProfileHubItems}, the menu of the «Профиль» screen.
 *
 * Order matters twice: it is the reading order of the sidebar, and it is the order
 * `ClientHeader` searches for the page title — so the most specific paths must never sit
 * behind a less specific one that also matches. `exact` on «Главная» is what guarantees
 * that here.
 */
export const clientNavigationItems: ClientNavItem[] = [
  {
    id: 'client-dashboard',
    label: 'Главная',
    path: ROUTES.client.dashboard,
    icon: Home,
    // `/client` is a prefix of every other portal path — see the module comment.
    exact: true,
  },
  {
    id: 'client-applications',
    label: 'Заявки',
    path: ROUTES.client.applications.list,
    icon: FileText,
  },
  {
    id: 'client-templates',
    group: 'reference',
    label: 'Шаблоны',
    path: ROUTES.client.templates,
    icon: Copy,
    // Release Р5 (r5-application-templates.md §5): a rarer-use screen than the five that
    // already fill the phone bar — kept to the sidebar only, same treatment «Уведомления»
    // gets below.
    showInBottomNav: false,
  },
  {
    id: 'client-documents',
    label: 'Документы',
    path: ROUTES.client.documents,
    icon: FolderOpen,
  },
  {
    id: 'client-containers',
    group: 'reference',
    label: 'Контейнеры',
    path: ROUTES.client.containers.list,
    // Same icon the internal directory uses for the same concept
    // (`sidebar-nav/navigationConfig.ts`'s `/directory/containers` entry).
    icon: Container,
    // Release Р4 (r4-fleet-is-scoped-to-our-own-service-history.md §6): the bottom bar is
    // already at its five-item capacity after Р2 — see the module docblock. Picked up by the
    // «Ещё» sheet, not built here.
    showInBottomNav: false,
    // The key existed from the day the roles matrix shipped and was read by nothing: «Просмотр
    // парка и ПТО» could be unticked and both sections stayed exactly where they were.
    requiresClientPermission: CPK.Fleet.View,
  },
  {
    id: 'client-ptos',
    group: 'reference',
    // «ПТО», exactly as the internal sidebar says it (31.08.2026, owner's decision reversing
    // Р34). The portal spent a week avoiding the abbreviation — «Пункты обслуживания» here, «Где
    // обслуживать» on the create form, «точки обслуживания» on the card — and the rows inside are
    // named «ПТО Владивосток» regardless, because that is the directory's own value. One word for
    // one thing, on both surfaces.
    label: 'ПТО',
    path: ROUTES.client.ptos,
    // `MapPin`, not the internal top-level `/pto` work-queue's `ClipboardCheck` — this screen
    // is the directory's «service point as a place» concept (`/directory/pto` uses the same
    // `MapPin`), not a checklist of in-progress inspections.
    icon: MapPin,
    showInBottomNav: false,
    requiresClientPermission: CPK.Fleet.View,
  },
  {
    id: 'client-messenger',
    label: 'Сообщения',
    path: ROUTES.client.messenger,
    icon: MessagesSquare,
  },
  {
    id: 'client-employees',
    group: 'company',
    label: 'Сотрудники',
    path: ROUTES.client.employees,
    icon: Users,
    // `/client/employees` — префикс `/client/employees/roles`, а у «Ролей и прав» свой пункт меню,
    // и без этого флага на них подсвечивались ОБА (27.08.2026). Тот же случай, что у «Главной»,
    // и лечится тем же флагом. Безопасно, пока каждый вложенный маршрут раздела имеет свой пункт:
    // сейчас под `/client/employees` живёт ровно один — «Роли и права».
    exact: true,
    // Never on the bar: five slots are the daily work, and this is a section a customer opens on
    // purpose, a few times a year.
    showInBottomNav: false,
    requiresClientPermission: CPK.Employees.View,
  },
  {
    id: 'client-roles',
    group: 'company',
    label: 'Роли и права',
    path: ROUTES.client.employeeRoles,
    icon: ShieldCheck,
    showInBottomNav: false,
    requiresClientPermission: CPK.Roles.MatrixView,
  },
  {
    id: 'client-notifications',
    group: 'personal',
    label: 'Уведомления',
    path: ROUTES.client.notifications,
    icon: Bell,
    // Six items, five slots. The bell in `ClientHeader` carries this one on the phone.
    showInBottomNav: false,
  },
  {
    id: 'client-profile',
    group: 'personal',
    label: 'Профиль',
    path: ROUTES.client.profile,
    icon: User,
    // Back on the bar (mobile rework, 2026-08-21) — it holds the fifth slot «Ещё» took
    // during Р4, and carries the four screens that sheet used to hide. See the module
    // docblock for why a sheet was the wrong shape for them.
  },
];

/**
 * The subset the phone's bottom bar renders — derived, never hand-written, so an item
 * added to {@link clientNavigationItems} cannot be forgotten here or duplicated with a
 * different label.
 */
export const clientBottomNavItems: ClientNavItem[] = clientNavigationItems.filter(
  (item) => item.showInBottomNav !== false
);

/**
 * The «Кабинет» menu of the «Профиль» screen — every item that stays off the bottom bar
 * («Шаблоны», «Контейнеры», «ПТО», «Уведомления»), in the same {@link clientNavigationItems}
 * order. Derived for the identical reason {@link clientBottomNavItems} is: the hub must not
 * be able to show a label, path or icon that disagrees with the sidebar's.
 *
 * The complement of {@link clientBottomNavItems} within the menu — every item is in exactly
 * one of the two, which is what guarantees no screen becomes unreachable on a phone.
 */
export const clientProfileHubItems: ClientNavItem[] = clientNavigationItems.filter(
  (item) => item.showInBottomNav === false
);

// ============================================================================
// Active-item helper
// ============================================================================

/**
 * Whether a portal nav item is the active one for the current path.
 *
 * `exact` items match the pathname and nothing else. Everything else is a prefix match on
 * a path SEGMENT boundary, so `/client/applications/new` and `/client/applications/{id}`
 * keep «Заявки» highlighted, while `/client/applications` never lights up for some future
 * `/client/applications-archive`.
 */
export function isClientNavItemActive(item: ClientNavItem, pathname: string): boolean {
  if (item.exact) return pathname === item.path;
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

/**
 * Whether the caller may see this item at all.
 *
 * Ungated items are always visible — which is every item the portal had before the roles release,
 * so nothing changes for a customer whose company never configures anything.
 *
 * Deliberately NOT folded into {@link clientBottomNavItems} / {@link clientProfileHubItems}: those
 * two answer «where does this item live», a question with the same answer for everybody, and the
 * invariant that every item is in exactly one of them is what guarantees no screen becomes
 * unreachable on a phone. Visibility is a second question, asked at render time.
 */
export function isClientNavItemVisible(
  item: ClientNavItem,
  can: (key: ClientPermissionKey) => boolean
): boolean {
  return item.requiresClientPermission === undefined || can(item.requiresClientPermission);
}

/**
 * The sidebar's blocks, derived from {@link clientNavigationItems} rather than listed again — a
 * second list is how a label, an icon or a path ends up disagreeing with the bar's.
 *
 * A block with no items is dropped, so removing the last «Справочники» screen removes its heading
 * too instead of leaving a title over nothing.
 */
/**
 * The computer's menu, as a TREE (2026-08-26, owner: one model on both surfaces — «пункт с
 * подпунктами или просто пункт»).
 *
 * It was three named blocks with headings; the internal rail had parents that expand. The owner
 * chose the second for both, so «Справочники» and «Компания» stopped being captions over rows and
 * became rows that open. Neither has a screen of its own — clicking one expands it, which is what
 * `SidebarNav` does with any node that has children.
 *
 * **Derived from `clientNavigationItems`, never written beside it.** That array stays flat and
 * untouched: the phone's bottom bar and `clientProfileHubItems` are derived from it too, and the
 * invariant those rely on — every screen is in exactly one of them — has nothing to do with how a
 * computer groups them. Adding an item to the array is still the only edit a new screen needs.
 */
export const clientSidebarTree: ClientNavItem[] = (() => {
  const inGroup = (group: ClientNavGroupId): ClientNavItem[] =>
    clientNavigationItems.filter((item) => (item.group ?? 'work') === group);

  const parents: Array<{ id: string; label: string; icon: ClientNavItem['icon']; group: ClientNavGroupId }> = [
    { id: 'client-reference', label: 'Справочники', icon: Library, group: 'reference' },
    { id: 'client-company', label: 'Компания', icon: Building2, group: 'company' },
  ];

  return [
    ...inGroup('work'),
    ...parents
      .map((parent) => ({
        id: parent.id,
        label: parent.label,
        icon: parent.icon,
        // The parent has no screen. The path is its first child's, so a keyboard user who
        // activates the row rather than expanding it still lands somewhere sensible — and
        // `SidebarNav` expands instead of following it.
        path: inGroup(parent.group)[0]?.path ?? ROUTES.client.dashboard,
        children: inGroup(parent.group),
      }))
      .filter((parent) => parent.children.length > 0),
  ];
})();
